import { strapiEndpoints } from './strapiFetch.config'
import { fetchFromStrapi } from '../graphql/client'

const useDynamicFetch = import.meta.env.VITE_APP_STRAPI_DYNAMIC === 'true'

const assetMapModules = import.meta.glob('../../../data/asset-map.json', { eager: true })
const assetMap: Record<string, string> =
    (Object.values(assetMapModules)[0] as { default: Record<string, string> })?.default ?? {}

let strapiContentCache: Record<string, unknown> | null = null

async function getStaticContent(): Promise<Record<string, unknown>> {
    if (!strapiContentCache) {
        const modules = import.meta.glob('../../../data/strapi-content.json', { eager: true, import: 'default' })
        strapiContentCache = modules['../../../data/strapi-content.json'] as Record<string, unknown>
    }
    return strapiContentCache
}

/**
 * Get content from Strapi with Zod validation.
 * - Dynamic mode (dev / build:dev): fetches via Apollo, validates with Zod
 * - Static mode (build): loads from prebaked JSON (already validated at build time)
 */
export async function getStrapiContent<T>(key: string): Promise<T> {
    const config = strapiEndpoints.find((e) => e.key === key)

    if (!config) {
        throw new Error(`Strapi endpoint not configured for key: ${key}`)
    }

    if (useDynamicFetch) {
        const data = await fetchFromStrapi(config.query, config.variables)

        if (config.schema) {
            const parseResult = config.schema.safeParse(data)

            if (!parseResult.success) {
                console.error(`Zod validation failed for ${key}:`, parseResult.error.format())
                throw new Error(`Validation failed for ${key}`)
            }

            return parseResult.data as T
        }

        return data as T
    }

    const content = await getStaticContent()
    const data = content[key]

    if (data === undefined) {
        throw new Error(`No prebaked content found for key: ${key}`)
    }

    return data as T
}

export function getStrapiMediaUrl(strapiPath: string | null | undefined): string {
    if (!strapiPath) return ''

    if (useDynamicFetch) {
        return `${import.meta.env.VITE_APP_STRAPI_URL}${strapiPath}`
    }

    return assetMap[strapiPath] ?? strapiPath
}

export function extractMediaUrl(media: { url?: string } | { url?: string }[] | null | undefined): string {
    if (!media) return ''

    if (Array.isArray(media)) {
        return media[0]?.url ? getStrapiMediaUrl(media[0].url) : ''
    }

    return media.url ? getStrapiMediaUrl(media.url) : ''
}
